import { useEffect, useState } from 'react';
import { api } from '../api.ts';
import { OBERFLAECHE } from '../teile/Stand.tsx';
import { versionsnummer } from '../teile/version.ts';
import { Marke } from '../teile/Marke.tsx';
import { Passwortregeln, usePasswortvorgabe } from '../teile/Passwortregeln.tsx';

/**
 * Der erste Bildschirm des Tages.
 *
 * Er hat genau eine Aufgabe. Alles, was nicht dem Anmelden dient, gehört
 * dahinter: kein Werbetext, kein Bild, keine zweite Möglichkeit.
 *
 * DREI DINGE, DIE HIER LEICHT SCHIEFGEHEN
 *
 *   1. Ein Fehler, der nur über dem Formular steht. Wer „Anmeldung
 *      fehlgeschlagen" liest, weiß nicht, ob die Adresse oder das Passwort
 *      gemeint ist. Der Server sagt es, und das Feld wird rot.
 *   2. Ein Knopf, der beim zweiten Klick ein zweites Mal anmeldet. Er sperrt
 *      sich, solange die Antwort aussteht.
 *   3. Ein Passwortverwalter, der nichts anbieten kann, weil die Felder keine
 *      Kennzeichnung tragen. autoComplete sagt ihm, was hineingehört.
 */
export default function Anmeldung(
  { fertig }: { fertig: (a?: { faktorOffen?: boolean; faktorEinrichten?: boolean }) => void }
) {
  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [feld, setFeld] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function absenden(e: React.FormEvent) {
    e.preventDefault();
    setFehler(null); setFeld(null); setLaeuft(true);
    try {
      fertig(await api.anmelden(email, passwort));
    } catch (f: any) {
      setFehler(f?.message ?? 'Die Anmeldung hat nicht geklappt.');
      // Der Server nennt das Feld, wenn er eines kennt. Tut er es nicht,
      // färben wir nichts: ein falsch gefärbtes Feld schickt in die Irre.
      setFeld(f?.daten?.feld ?? null);
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="anmeldeseite">
      <form className="karte anmeldekarte" onSubmit={absenden}>
        <div className="leib">
          <div className="marke">
            <Marke gross />
            <div>
              <b>MDS FTTH</b>
              <span>Auftragsverwaltung</span>
            </div>
          </div>

          {fehler && (
            <div className="hinweis fehler" role="alert">{fehler}</div>
          )}

          <label htmlFor="email">Mailadresse</label>
          <input
            id="email" type="email" autoComplete="username" autoFocus
            className={feld === 'email' ? 'falsch' : undefined}
            value={email} onChange={(e) => setEmail(e.target.value)}
          />

          <label htmlFor="passwort">Passwort</label>
          <input
            id="passwort" type="password" autoComplete="current-password"
            className={feld === 'passwort' ? 'falsch' : undefined}
            value={passwort} onChange={(e) => setPasswort(e.target.value)}
          />

          <button className="knopf haupt" disabled={laeuft || !email || !passwort}>
            {laeuft ? 'Einen Moment' : 'Anmelden'}
          </button>
        </div>
      </form>

      <div className="stand">Version {versionsnummer(OBERFLAECHE)}</div>
    </div>
  );
}


/**
 * Ein Passwort über einen Einladungslink setzen.
 *
 * ORIGINALTEXT MARVIN (27.08.2026)
 *   „wenn ich einem neuen benutzer anlege dann soll er eine email erhalten, wo
 *    ein link drin ist, wo er sein passwort setzen muss."
 *
 * DIESER BILDSCHIRM MELDET NIEMANDEN AN, und das steht auch darauf. Nach dem
 * Setzen führt er zur Anmeldung, mit vorausgefüllter Mailadresse - der Weg
 * herein bleibt Mailadresse und Passwort, für jeden. Ein Bildschirm, der nach
 * dem Setzen einfach in die Anwendung springt, hätte aus dem Link doch wieder
 * einen Anmeldelink gemacht.
 *
 * ZWEIMAL EINGEBEN. Wer sich hier vertippt, hat sich aus einem Zugang
 * ausgesperrt, den er noch nie benutzt hat, und braucht eine neue Einladung
 * aus dem Büro. Ein zweites Feld kostet fünf Sekunden.
 */
export function Einladung(
  { token, fertig }: { token: string; fertig: () => void }
) {
  const [stand, setStand] = useState<any>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [feld, setFeld] = useState<string | null>(null);
  const [passwort, setPasswort] = useState('');
  const [nochmal, setNochmal] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fertigGesetzt, setFertigGesetzt] = useState(false);
  const [verstoesse, setVerstoesse] = useState<string[]>([]);
  const vorgabe = usePasswortvorgabe();

  useEffect(() => {
    api.einladungStand(token)
      .then(setStand)
      .catch((f: any) => {
        setStand({ gilt: false });
        setFehler(f?.message ?? 'Dieser Link zieht nicht mehr.');
        setHinweis(f?.daten?.hinweis ?? null);
      });
  }, [token]);

  /* DIE MINDESTLAENGE KOMMT VOM SERVER, an zwei Wegen: `/einladung/:token`
     nennt sie mit, und `/passwortregeln` sowieso. Die Zahl steht an KEINER
     Stelle in dieser Datei - hier stand „10", als der Server längst zwölf
     verlangte. */
  const mindest = stand?.mindestlaenge ?? vorgabe?.mindestlaenge ?? 12;

  async function setzen(e: React.FormEvent) {
    e.preventDefault();
    setFehler(null); setFeld(null); setVerstoesse([]); setLaeuft(true);
    try {
      const a = await api.einladungSetzen(token, passwort);
      setFertigGesetzt(true);
      setHinweis(a?.hinweis ?? null);
    } catch (f: any) {
      setFehler(f?.message ?? 'Das Passwort ließ sich nicht setzen.');
      setHinweis(f?.daten?.hinweis ?? null);
      setFeld(f?.daten?.feld ?? null);
      setVerstoesse(f?.daten?.verstoesse ?? []);
    } finally {
      setLaeuft(false);
    }
  }

  const gleich = passwort.length > 0 && passwort === nochmal;

  return (
    <div className="anmeldeseite">
      <div className="karte anmeldekarte">
        <div className="leib">
          <div className="marke">
            <Marke gross />
            <div>
              <b>MDS FTTH</b>
              <span>Passwort setzen</span>
            </div>
          </div>

          {fehler && <div className="hinweis fehler" role="alert">{fehler}</div>}
          {hinweis && <div className="hinweis">{hinweis}</div>}

          {!stand && <p className="klein leise">Wird geprüft.</p>}

          {fertigGesetzt && (
            <button className="knopf haupt" onClick={fertig}>
              Jetzt anmelden
            </button>
          )}

          {stand?.ok && !fertigGesetzt && (
            <form onSubmit={setzen}>
              <p className="klein leise" style={{ marginBottom: 12 }}>
                Für <b>{stand.name}</b> ({stand.email}) wurde ein Zugang
                angelegt. Setzen Sie hier Ihr eigenes Passwort - niemand im
                Büro kennt es danach.
              </p>

              <label htmlFor="neu">Neues Passwort</label>
              <input id="neu" type="password" autoComplete="new-password"
                     autoFocus
                     className={feld === 'passwort' ? 'falsch' : undefined}
                     value={passwort}
                     onChange={(e) => setPasswort(e.target.value)} />

              <label htmlFor="nochmal">Noch einmal</label>
              <input id="nochmal" type="password" autoComplete="new-password"
                     className={nochmal && !gleich ? 'falsch' : undefined}
                     value={nochmal}
                     onChange={(e) => setNochmal(e.target.value)} />

              {/* DIE VORGABE STEHT DA, BEVOR GETIPPT WIRD. */}
              <Passwortregeln wert={passwort} verstoesse={verstoesse} schmal />

              <button className="knopf haupt"
                      disabled={laeuft || passwort.length < mindest || !gleich}>
                {laeuft ? 'Einen Moment' : 'Passwort setzen'}
              </button>
            </form>
          )}

          {stand && !stand.ok && !fertigGesetzt && (
            <button className="knopf" onClick={fertig}>Zur Anmeldung</button>
          )}
        </div>
      </div>

      <div className="stand">Version {versionsnummer(OBERFLAECHE)}</div>
    </div>
  );
}


/**
 * Das geliehene Passwort wechseln - bevor irgendetwas anderes geht.
 *
 * ORIGINALTEXT MARVIN (27.08.2026)
 *   „wenn ich das passwort setze im system wird der benutzer beim nächsten
 *    login aufgefordert das passwort zu ändern."
 *
 * DIESER BILDSCHIRM IST NICHT DER RIEGEL. Der sitzt im Server, vor jeder
 * Anfrage (`server.ts`, `OHNE_WECHSEL_ERLAUBT`). Wer dieses Fenster
 * wegklickt, kommt an keine einzige Zahl heran - dieselbe Bauart wie beim
 * zweiten Faktor, und aus demselben Grund: eine Aufforderung in der Anzeige
 * wäre keine.
 *
 * DAS ALTE PASSWORT GEHÖRT DAZU, auch hier. Es ist genau das, das im Büro
 * gesetzt und am Telefon gesagt wurde - wer es nicht hat, sitzt nicht vor
 * diesem Bildschirm.
 */
export function PasswortWechsel(
  { name, fertig }: { name: string; fertig: () => void }
) {
  const [alt, setAlt] = useState('');
  const [neu, setNeu] = useState('');
  const [nochmal, setNochmal] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [feld, setFeld] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [verstoesse, setVerstoesse] = useState<string[]>([]);
  const vorgabe = usePasswortvorgabe();
  const mindest = vorgabe?.mindestlaenge ?? 12;

  async function absenden(e: React.FormEvent) {
    e.preventDefault();
    setFehler(null); setHinweis(null); setFeld(null); setVerstoesse([]);
    setLaeuft(true);
    try {
      await api.meinPasswort(alt, neu);
      fertig();
    } catch (f: any) {
      setFehler(f?.message ?? 'Das hat nicht geklappt.');
      setHinweis(f?.daten?.hinweis ?? null);
      setFeld(f?.daten?.feld ?? null);
      setVerstoesse(f?.daten?.verstoesse ?? []);
    } finally {
      setLaeuft(false);
    }
  }

  const gleich = neu.length > 0 && neu === nochmal;

  return (
    <div className="anmeldeseite">
      <form className="karte anmeldekarte" onSubmit={absenden}>
        <div className="leib">
          <div className="marke">
            <Marke gross />
            <div>
              <b>MDS FTTH</b>
              <span>Passwort ändern</span>
            </div>
          </div>

          <div className="hinweis warnung">
            Guten Tag {name}. Dieses Passwort wurde im Büro für Sie gesetzt und
            ist damit 2 Menschen bekannt. Bitte setzen Sie jetzt ein
            eigenes; danach geht es normal weiter.
          </div>

          {fehler && <div className="hinweis fehler" role="alert">{fehler}</div>}
          {hinweis && <div className="hinweis">{hinweis}</div>}

          <label htmlFor="alt">Bisheriges Passwort</label>
          <input id="alt" type="password" autoComplete="current-password"
                 autoFocus
                 className={feld === 'alt' ? 'falsch' : undefined}
                 value={alt} onChange={(e) => setAlt(e.target.value)} />

          <label htmlFor="wneu">Neues Passwort</label>
          <input id="wneu" type="password" autoComplete="new-password"
                 className={feld === 'neu' ? 'falsch' : undefined}
                 value={neu} onChange={(e) => setNeu(e.target.value)} />

          <label htmlFor="wnochmal">Noch einmal</label>
          <input id="wnochmal" type="password" autoComplete="new-password"
                 className={nochmal && !gleich ? 'falsch' : undefined}
                 value={nochmal} onChange={(e) => setNochmal(e.target.value)} />

          <Passwortregeln wert={neu} verstoesse={verstoesse} schmal />
          <p className="klein leise" style={{ margin: '8px 0 12px' }}>
            Alle anderen Sitzungen enden dabei.
          </p>

          <button className="knopf haupt"
                  disabled={laeuft || !alt || neu.length < mindest || !gleich}>
            {laeuft ? 'Einen Moment' : 'Passwort ändern'}
          </button>
        </div>
      </form>

      <div className="stand">Version {versionsnummer(OBERFLAECHE)}</div>
    </div>
  );
}
